import React, { useEffect, useState } from 'react';
import { Box, Button, Card, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import Paginations from '../../common/pagination/Paginations';
import ForumModal from './ForumModal';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createForumApi, deleteForumApi, getAllContentModerationForum, updateFourmApi } from '../../api';
import ReyLeaf from "../../common/assests/reyleafLogo.svg";
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import configuration from '../../config/Config';
import { AdminTable, ActionCell } from '../../common/adminTable';
import { formatCount } from '../../utils/formatCount';

const ContentModration = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [contentModrationData, setContentModrationData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(false);

  const handleContentModeration = async () => {
    try {
      setLoading(true);
      const response = await getAllContentModerationForum({ token, currentPage });
      setContentModrationData(response?.data);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleContentModeration();
  }, [currentPage, change]);

  const handleClickOpen = (data) => {
    setEditingData(data || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async (forumData, id) => {
    try {
      if (id) {
        if (!forumData.title || !forumData.description) {
          toast.warning("Title and description required");
          return;
        }
        const response = await updateFourmApi({ token, forumData, id });
        toast.success(response?.message);
        handleClose();
      } else {
        if (!forumData.title || !forumData.description || !forumData.picture) {
          toast.warning("Image, title and description required");
          return;
        }
        const response = await createForumApi({ token, forumData });
        toast.success(response?.message);
        setEditingData(null);
        handleClose();
      }
      setChange(!change);
    } catch (error) {
      toast.error(error?.message || error?.error);
      console.error("Error saving forum", error);
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({ title: "Delete Forum", description: "Are you sure you want to delete this forum?", confirmText: "Delete", destructive: true });
    if (!isConfirmed) return;
    try {
      const response = await deleteForumApi({ token, id });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      if (error?.status === 500) {
        toast.error("Some error occured");
      } else {
        toast.error(error?.message || error?.error);
      }
      console.log("error", error);
    }
  };

  const handleViewDetails = (event) => {
    navigate("/content-moderation/details", { state: { event } });
  };

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const columns = [
    {
      field: "picture",
      headerName: "Image",
      width: 80,
      renderCell: (row) => (
        <img
          src={row?.picture ? `${configuration.cdnFront}/${row.picture}` : ReyLeaf}
          alt="forum"
          style={{ height: 48, width: 48, borderRadius: "8px", objectFit: "cover" }}
        />
      ),
    },
    {
      field: "title",
      headerName: "Title",
      minWidth: 160,
      renderCell: (row) => row?.title || "-",
    },
    {
      field: "members",
      headerName: "Members",
      width: 110,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#065F46" }}>
          <PeopleIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: "13px", fontFamily: "Heebo" }}>
            {formatCount(row?.members?.length)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 200,
      renderCell: (row) => {
        const desc = row?.description || "-";
        return desc.length > 100 ? `${desc.substring(0, 100)}...` : desc;
      },
    },
    {
      field: "__actions",
      headerName: "",
      width: 120,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            { label: "View", icon: <VisibilityIcon fontSize="small" />, color: "#065F46", onClick: () => handleViewDetails(row) },
            { label: "Edit", icon: <EditIcon fontSize="small" />, color: "#065F46", onClick: () => handleClickOpen(row) },
            { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "#EA001B", onClick: () => handleDelete(row?._id) },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginBottom: "30px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, marginBottom: "20px" }}>
          <Typography sx={{ fontSize: { xs: "16px", sm: "20px" }, fontWeight: "600", color: "#362B24" }}>
            All Forums
          </Typography>
          <Button
            onClick={() => handleClickOpen(null)}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: "29px",
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              boxShadow: "1px 2px 30px 0px rgba(0, 0, 0, 0.06)",
              color: "white",
              textTransform: "none",
              fontSize: "15px",
              px: 3,
              height: "44px",
            }}
          >
            Create New Forum
          </Button>
        </Box>

        <AdminTable
          columns={columns}
          rows={contentModrationData}
          loading={loading}
          rowKey="_id"
          emptyText="No forums found"
        />

        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          disabled={loading}
        />
      </Card>

      <ForumModal
        open={open}
        handleClose={handleClose}
        handleSave={handleSave}
        editingData={editingData}
      />
    </div>
  );
};

export default ContentModration;
